import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Security,
  Warning,
  Info,
  CheckCircle,
  Email,
  Phone,
  ArrowForward
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const PrivacyPolicy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeSection, setActiveSection] = useState('section-1');

  // 페이지 진입 시 항상 최상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 스크롤 감지 로직
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          current = section.getAttribute('id');
        }
      });

      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth',
      });
      setActiveSection(id);
    }
  };

  // 공통 컴포넌트: 섹션 타이틀
  const SectionTitle = ({ children }) => (
    <Box sx={{ mb: 3, mt: 6, borderBottom: 1, borderColor: 'divider', pb: 1 }}>
      <Typography variant="h5" component="h2" fontWeight="bold" color="text.primary">
        {children}
      </Typography>
    </Box>
  );

  // 공통 컴포넌트: 서브 타이틀
  const SubTitle = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1 }}>
      <CheckCircle fontSize="small" color="primary" sx={{ mr: 1 }} />
      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );

  // 내비게이션 아이템 데이터
  const navItems = [
    { id: 'section-1', label: '1. 수집하는 정보' },
    { id: 'section-2', label: '2. 민감정보 처리 (중요)' },
    { id: 'section-3', label: '3. 수집 목적' },
    { id: 'section-4', label: '4. AI 모델 관련 고지' },
    { id: 'section-5', label: '5. 보관 및 파기' },
    { id: 'section-6', label: '6. 제3자 제공' },
    { id: 'section-7', label: '7. 책임 제한 및 주의' },
    { id: 'section-8', label: '8. 이용자의 권리' },
    { id: 'section-9', label: '9. 아동 이용 제한' },
    { id: 'section-10', label: '10. 문의처' },
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 5 }}>
      <Container maxWidth="lg">
        {/* 헤더 */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h1" fontWeight="800" gutterBottom>
            개인정보 처리방침
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            주식회사 가람마인드 (이하 “회사”)
          </Typography>
          <Chip 
            icon={<Security />} 
            label="이용자의 소중한 정서·심리 정보를 안전하게 보호합니다." 
            color="primary" 
            variant="outlined" 
            sx={{ mt: 2, px: 1, py: 2.5, borderRadius: 8 }} 
          />
        </Box>

        <Grid container spacing={4}>
          {/* 사이드 내비게이션 (데스크탑: Sticky, 모바일: 상단 배치) */}
          <Grid item xs={12} md={3}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                position: isMobile ? 'static' : 'sticky', 
                top: 100,
                bgcolor: 'white',
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ px: 2, mb: 1, display: 'block' }}>
                목차
              </Typography>
              <List component="nav" dense>
                {navItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeSection === item.id}
                    onClick={() => scrollToSection(item.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.light' },
                      },
                    }}
                  >
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* 본문 콘텐츠 */}
          <Grid item xs={12} md={9}>
            <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
              
              <Typography paragraph color="text.secondary">
                본 개인정보 처리방침은 <strong>말랑포켓</strong> 서비스 내에서 수집·이용되는 정보, 특히 정서·심리 상태와 관련된 민감한 정보를 어떻게 처리하는지에 대한 회사의 원칙과 절차를 규정합니다. 
              </Typography>

              {/* 1. 수집하는 정보 */}
              <section id="section-1">
                <SectionTitle>1. 수집하는 정보</SectionTitle>
                 <Typography paragraph>회사는 서비스 제공 및 품질 향상을 위하여 아래와 같은 정보를 수집합니다. </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>필수 수집 정보 </Typography>
                      <List dense>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, fontSize: '0.875rem' }}>
                          계정: 이메일, 아이디, 비밀번호, 닉네임, 연령대, 성별
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, fontSize: '0.875rem', mt: 1 }}>
                          심리 정보: 감정일기, 감정 평정, 검사 점수(PHQ-9 등), 위험 신호
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, fontSize: '0.875rem', mt: 1 }}>
                          AI 상담: 대화 내용 로그, 메타데이터 
                        </ListItem>
                      </List>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>선택 수집 정보</Typography>
                      <List dense>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, fontSize: '0.875rem' }}>
                          이벤트 참여 시 연락처 등
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, fontSize: '0.875rem', mt: 1 }}>
                          연구 참여 시 추가 심리·환경 정보 
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, fontSize: '0.875rem', mt: 1 }}>
                          문의/상담 시 자발적 입력 내용 
                        </ListItem>
                      </List>
                    </Box>
                  </Grid>
                </Grid>
              </section>

              {/* 2. 민감정보 처리 고지 */}
              <section id="section-2">
                <Box sx={{ mt: 6 }}>
                  <Alert severity="warning" variant="outlined" icon={<Warning fontSize="inherit" />}>
                    <AlertTitle sx={{ fontWeight: 'bold' }}>2.  민감정보 처리 고지</AlertTitle>
                    <Typography variant="body2" paragraph>
                      이 서비스는 이용자의 정서·심리 상태를 돕기 위해 <strong>건강정보에 준하는 민감정보</strong>(우울·불안 척도, 자살사고 관련 정보 등)를 처리할 수 있습니다.
                    </Typography>
                    <Box sx={{ mt: 1, bgcolor: 'warning.light', p: 2, borderRadius: 1, opacity: 0.9 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="warning.contrastText">회사의 처리 원칙</Typography>
                      <Typography variant="caption" display="block" color="warning.contrastText">
                        • <strong>명확한 동의:</strong> 별도의 동의 절차를 거친 후에만 처리합니다.
                      </Typography>
                      <Typography variant="caption" display="block" color="warning.contrastText">
                        • <strong>최소 수집:</strong> 목적 달성 시 즉시 파기 또는 비식별화합니다.
                      </Typography>
                      <Typography variant="caption" display="block" color="warning.contrastText">
                        • <strong>엄격한 보안:</strong> 강화된 접근 통제 및 암호화를 적용합니다.
                      </Typography>
                    </Box>
                  </Alert>
                </Box>
              </section>

              {/* 3. 수집 목적 */}
              <section id="section-3">
                <SectionTitle>3.  수집 목적 </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <SubTitle>정서 및 심리 상태 분석</SubTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                       기분 흐름 파악, 정서 증상 경향성 이해, 자기 관리 피드백 제공, 위험 신호 조기 탐지 및 안내
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                     <SubTitle>서비스 운영 및 품질 개선 </SubTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                       AI 대화 기능 제공, 통계 분석, 알고리즘 성능 개선(비식별화 후 활용), 오류 수정
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                     <SubTitle>법적 의무 및 분쟁 대응</SubTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                       관련 법령상 보관 의무 이행 및 약관 위반 행위 조사
                    </Typography>
                  </Grid>
                </Grid>
              </section>

              {/* 4. AI 모델 관련 고지 */}
              <section id="section-4">
                <SectionTitle>4.  AI 모델 관련 고지 </SectionTitle>
                <Alert severity="info" icon={<Info />}>
                   <AlertTitle>외부 AI 모델 이용 현황 및 자체 모델 전환 계획  </AlertTitle>
                  <List dense>
                    <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                       회사는 현재 AI 상담 응답 생성을 위해 <strong>외부 AI 사업자(OpenAI 등)의 언어모델을 API 형태로 이용</strong>하고 있으며, 향후 회사가 자체적으로 구축하는 <strong>내부 오픈소스 기반 AI 모델로 순차 전환</strong>할 예정입니다.
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                       외부 AI 사업자를 이용하는 동안 회사는 이용자의 데이터를 <strong>외부 AI 모델의 학습(Fine-tuning) 목적으로 사용하지 않도록</strong> 조치하며, 응답 생성에 필요한 최소한의 텍스트 데이터만 암호화하여 전송합니다.
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                       위와 같은 이유로 서비스 제공을 위해 암호화된 텍스트 데이터가 국외 서버(예: OpenAI 미국 서버)로 전송될 수 있으며, 자체 AI 모델 전환이 완료되면 해당 국외 전송은 종료되고 회사 내부 인프라에서만 처리됩니다.
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                       회사가 AI 모델의 성능 개선 등 인공지능 기술 개발/연구를 위해 이용자의 데이터를 활용하는 경우, 개인을 식별할 수 없도록 <strong>익명처리(비식별화)한 정보만을 사용</strong>하며, 「개인정보 보호법」 제28조의12부터 제28조의15까지 정한 안전조치 및 절차(위험요인 평가, 개인정보보호위원회 심의·의결 등)를 준수합니다.
                    </ListItem>
                  </List>
                </Alert>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  *  AI 응답은 통계적 추론에 기반하므로 항상 정확하지 않을 수 있으며, 전문적인 의학적 진단이 아닙니다.
                </Typography>
              </section>

              {/* 5. 보관 및 파기 */}
              <section id="section-5">
                <SectionTitle>5.  보관 및 파기 정책 </SectionTitle>
                 <Typography paragraph>수집 목적이 달성되면 개인정보를 지체 없이 파기하는 것을 원칙으로 합니다. </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                      <TableRow>
                        <TableCell width="30%"><strong>구분</strong></TableCell>
                        <TableCell><strong>보관 기간</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          서비스 데이터<br/>
                          <Typography variant="caption" color="text.secondary">(감정일기, 대화로그 등)</Typography>
                        </TableCell>
                        <TableCell>
                          서비스 이용 기간 동안 보관<br/>
                           <Typography variant="caption" color="text.secondary">(탈퇴 요청 시 지체 없이 삭제 또는 비식별화)</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>법적 의무 기록</TableCell>
                         <TableCell>관련 법령에 따른 기간 (예: 통신비밀보호법 등) </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </section>

              {/* 6. 제3자 제공 */}
              <section id="section-6">
                <SectionTitle>6.  제3자 제공 여부 </SectionTitle>
                 <Typography paragraph>원칙적으로 제3자에게 제공하지 않으나, 아래의 경우 예외가 적용됩니다.  </Typography>
                <List>
                  <ListItem>
                    <ArrowForward fontSize="small" color="action" sx={{ mr: 1 }} />
                    <ListItemText
                      primary="AI 기술 제공사(OpenAI 등)"
                      secondary="현재 AI 상담 응답 생성을 위해 암호화된 최소한의 텍스트 데이터를 전송하며, 향후 자체 AI 모델 전환 시 이 제공은 종료될 예정"
                    />
                  </ListItem>
                  <ListItem>
                    <ArrowForward fontSize="small" color="action" sx={{ mr: 1 }} />
                    <ListItemText
                      primary="공공·민간 기관 연계 (병원 등)"
                      secondary="이용자의 명시적 요청과 동의가 있는 경우에 한함"
                    />
                  </ListItem>
                  <ListItem>
                    <ArrowForward fontSize="small" color="action" sx={{ mr: 1 }} />
                    <ListItemText
                      primary="법적 요청"
                      secondary="수사기관 등이 정당한 절차로 요청하는 경우"
                    />
                  </ListItem>
                </List>
              </section>

              {/* 7. 책임 제한 (중요) */}
              <section id="section-7">
                <Box sx={{ mt: 6 }}>
                  <Alert severity="error" variant="filled" icon={<Warning fontSize="inherit" />}>
                     <AlertTitle variant="h6">7. 위험 상황 안내 및 의료행위 아님 고지</AlertTitle>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom> 의료행위가 아닙니다</Typography>
                      <Typography variant="body2" paragraph>
                        본 서비스는 의료법상 의료행위나 심리치료를 대체하지 않으며, 질병에 대한 진단·처방을 제공하지 않습니다. 
                         중요한 의료적 결정은 반드시 전문가와 상의해야 합니다.  
                      </Typography>
                      
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom> 응급지원 시스템이 아닙니다</Typography>
                      <Typography variant="body2">
                         자살·자해 등 급박한 위기 상황에 대해 실시간 개입을 제공하지 않습니다. 
                         위급 상황 시 즉시 <strong>112, 119, 109</strong> 등 관할 기관에 연락하십시오. 
                      </Typography>
                    </Box>
                  </Alert>
                </Box>
              </section>

              {/* 8. 이용자의 권리 */}
              <section id="section-8">
                <SectionTitle>8.  이용자의 권리  </SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 2 }}>
                     열람·정정·삭제 요청: 언제든지 앱 내 설정 또는 문의처를 통해 요청 가능합니다. 
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 2 }}>
                     동의 철회 및 탈퇴: 언제든지 동의를 철회할 수 있으며, 탈퇴 시 관련 정보는 파기되거나 비식별화됩니다.  
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 2 }}>
                     민감정보 통제권: 민감정보 처리에 대해 별도로 활용 범위 축소 등을 요청할 수 있습니다.  
                  </ListItem>
                </List>
              </section>

              {/* 9. 아동 이용 제한 */}
              <section id="section-9">
                <SectionTitle>9.  아동·청소년 이용 제한  </SectionTitle>
                <Typography>
                  본 서비스는 <strong>만 14세 이상</strong>을 대상으로 합니다.  만 14세 미만의 가입은 원칙적으로 허용하지 않으며 확인 시 즉시 삭제합니다.
                </Typography>
              </section>

              {/* 10. 문의처 */}
              <section id="section-10">
                <SectionTitle>10.  개인정보 보호 책임자 및 문의  </SectionTitle>
                <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Typography paragraph color="text.secondary">
                     개인정보와 관련된 문의사항은 아래 연락처로 문의해 주시기 바랍니다.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">책임자 / 직위</Typography>
                       <Typography variant="body2">홍수희 / 사원  </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">소속</Typography>
                       <Typography variant="body2">주식회사 가람마인드  </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2">hong9849@garam-mind.com  </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2">010-9424-3355  </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
                <Typography variant="caption" display="block" align="right" sx={{ mt: 2, color: 'text.disabled' }}>
                  공고일자: 2026년 1월 17일 / 시행일자: 2026년 1월 17일
                </Typography>
              </section>

            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};


PrivacyPolicy.propTypes = {
    children: PropTypes.any,
};

export default PrivacyPolicy;